import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router";
import { api } from "../../api";
import { estimateLabel } from "../../estimate";
import { duration } from "../../lib/format";
import { issueKey } from "../../lib/tasks";
import { useFactory } from "../../store";
import type { RunHistoryItem } from "../../types";
import { ColorDot, StatusBadge, ToneBadge } from "../status-badge";
import { Disclosure } from "../tasks/run-row";
import { StationRunDetail } from "../tasks/station-detail";
import { TimeAgo } from "../time-ago";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { Skeleton } from "../ui/skeleton";

/**
 * One run in full, opened from the runs list. `row` is the list's copy when the run is on a
 * loaded page; a shared link to a run further down reads its work order instead.
 */
export function RunSheet({ runId, row }: { runId: string; row: RunHistoryItem | undefined }) {
  const { agentById } = useFactory();
  const detail = useQuery({ queryKey: ["run", runId], queryFn: () => api.run(runId) });
  const run = detail.data?.run;
  const taskId = row?.taskId ?? run?.taskId;
  const workOrder = useQuery({
    queryKey: ["task", taskId],
    queryFn: () => api.task(taskId!),
    enabled: !row && Boolean(taskId),
  });
  const task = row?.task ?? workOrder.data?.task;
  const agentId = row?.agentId ?? run?.agentId;
  const agent = agentId ? agentById.get(agentId) : undefined;
  const shown = run ?? row;
  const estimate = estimateLabel(run?.structured);
  const key = task ? issueKey(task) : null;

  return (
    <>
      <SheetHeader className="border-b p-6">
        <p className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          {agent && <ColorDot color={agent.station.color} className="size-2 ring-0" />}
          {agent?.name ?? agentId ?? "Run"}
          {shown && (
            <>
              <span aria-hidden="true">·</span>
              <TimeAgo at={shown.startedAt ?? shown.createdAt} />
              {shown.startedAt && (
                <>
                  <span aria-hidden="true">·</span>
                  {duration(shown.startedAt, shown.finishedAt)}
                </>
              )}
            </>
          )}
        </p>
        <SheetTitle className="text-xl">{task ? task.title : <Skeleton className="h-6 w-64" />}</SheetTitle>
        <SheetDescription className="font-mono text-xs">
          {key ?? (task ? "Manual work order" : "")}
        </SheetDescription>
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          {shown && <StatusBadge status={shown.status} />}
          {shown?.verdict && <ToneBadge tone="violet">{shown.verdict}</ToneBadge>}
          {shown && shown.attempt > 1 && <ToneBadge tone="amber">Attempt {shown.attempt}</ToneBadge>}
          {estimate && <ToneBadge tone="sky">{estimate}</ToneBadge>}
          {shown && <Badge variant="secondary">{shown.provider}</Badge>}
          {taskId && (
            <Button asChild variant="outline" size="sm" className="ml-auto h-7">
              <Link to={`/tasks/${taskId}`}>
                Open work order <ArrowUpRight />
              </Link>
            </Button>
          )}
        </div>
      </SheetHeader>
      <ScrollArea className="min-h-0 flex-1">
        <div className="px-3 py-3">
          {run ? (
            <StationRunDetail
              run={run}
              task={{ id: run.taskId }}
              agent={agent}
              framed="panel"
              actions={false}
            >
              {run.prompt && <Disclosure label="Prompt" text={run.prompt} />}
              {run.resultText && <Disclosure label="Final message" text={run.resultText} />}
            </StationRunDetail>
          ) : detail.isError ? (
            <p className="p-3 text-sm text-destructive">Could not load this run.</p>
          ) : (
            <div className="space-y-3 p-3">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-28 w-full" />
            </div>
          )}
        </div>
      </ScrollArea>
    </>
  );
}
